import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Form,
  Modal,
  Spinner,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaSearch,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import UserGroupApi from '../../../api/UserGroupApi';
import UserGroupForm from './UserGroupForm';
import './css/UserGroupManager.css';

/** Build a single message from axios error or non-success API body (express-validator errors array, etc.). */
function getUserGroupSaveErrorMessage(err, responseData) {
  const data = responseData ?? err?.response?.data;
  if (data) {
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const parts = data.errors
        .map(e => (e.msg || e.message || '').trim())
        .filter(Boolean);
      if (parts.length) return parts.join('. ');
    }
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message.trim();
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error.trim();
    }
  }
  if (err?.message) return err.message;
  return 'Failed to save group';
}

const UserGroupManager = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all'); // all, active, inactive
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  // Fetch groups on component mount
  useEffect(() => {
    fetchAllGroups();
  }, []);

  const fetchAllGroups = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching all user groups...');
      const userGroupApi = new UserGroupApi();
      const response = await userGroupApi.fetchAll(false); // Get all groups (active and inactive)

      console.log('Fetch all groups response:', response);

      if (response?.data?.success) {
        const groupsData = response.data.data || [];
        console.log(`Successfully fetched ${groupsData.length} groups`);
        setGroups(groupsData);
      } else {
        const errorMsg = response?.data?.message || 'Failed to fetch user groups';
        console.error('Fetch failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);

      let errorMessage = 'Failed to fetch user groups';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setError('');
    setShowModal(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setError('');
    setShowModal(true);
  };

  const handleSaveGroup = async (groupData, setModalError) => {
    try {
      console.log('Saving group data:', groupData);
      const userGroupApi = new UserGroupApi();
      let response;

      if (editingGroup) {
        // Update existing group
        response = await userGroupApi.updateGroup(editingGroup.id, groupData);
      } else {
        // Create new group
        response = await userGroupApi.createGroup(groupData);
      }

      console.log('Save response:', response);

      if (response?.data?.success) {
        setSuccess(editingGroup ? 'Group updated successfully!' : 'Group created successfully!');
        setShowModal(false);
        fetchAllGroups();
        setTimeout(() => setSuccess(''), 3000);
        return { success: true };
      }

      const errorMessage = getUserGroupSaveErrorMessage(null, response?.data);
      setModalError(errorMessage);
      return { success: false, error: errorMessage };
    } catch (err) {
      console.error('Error saving group:', err);

      const errorMessage = getUserGroupSaveErrorMessage(err);
      setModalError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const handleDeleteClick = (group) => {
    setGroupToDelete(group);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;

    try {
      const userGroupApi = new UserGroupApi();
      const response = await userGroupApi.deleteGroup(groupToDelete.id);

      if (response?.data?.success) {
        setSuccess('Group deleted successfully!');
        setShowDeleteConfirm(false);
        setGroupToDelete(null);
        fetchAllGroups();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorMsg = response?.data?.message || 'Failed to delete group';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error deleting group:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete group';
      setError(errorMessage);
    }
  };

  const handleToggleStatus = async (group) => {
    try {
      const userGroupApi = new UserGroupApi();
      const response = await userGroupApi.updateGroup(group.id, {
        is_active: !group.is_active
      });

      if (response?.data?.success) {
        setSuccess(`Group ${group.is_active ? 'deactivated' : 'activated'} successfully!`);
        fetchAllGroups();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorMsg = response?.data?.message || 'Failed to update group status';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error toggling group status:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update group status';
      setError(errorMessage);
    }
  };

  // Filter and search groups
  const getFilteredGroups = () => {
    let filtered = [...groups];

    // Apply active filter
    if (filterActive === 'active') {
      filtered = filtered.filter(g => g.is_active);
    } else if (filterActive === 'inactive') {
      filtered = filtered.filter(g => !g.is_active);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(g =>
        g.name?.toLowerCase().includes(term) ||
        g.description?.toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const filteredGroups = getFilteredGroups();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <Container fluid className="user-group-manager">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <FaUsers className="me-2" />
                User Groups
              </h2>
              <p className="text-muted">Organize users into groups for easier management</p>
            </div>
            <Button variant="primary" onClick={handleCreateGroup}>
              <FaPlus className="me-2" />
              Create Group
            </Button>
          </div>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters and Search */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FaSearch className="me-2" />
                  Search Groups
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  <FaFilter className="me-2" />
                  Filter by Status
                </Form.Label>
                <Form.Select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                >
                  <option value="all">All Groups</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Groups Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading groups...</p>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="text-center py-5">
              <FaUsers size={50} className="text-muted mb-3" />
              <h5>No groups found</h5>
              <p className="text-muted">
                {searchTerm || filterActive !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first user group to get started'}
              </p>
              {!searchTerm && filterActive === 'all' && (
                <Button variant="primary" onClick={handleCreateGroup} className="mt-3">
                  <FaPlus className="me-2" />
                  Create First Group
                </Button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th className="text-center">Members</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Created</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.map((group) => (
                    <tr key={group.id}>
                      <td>
                        <strong>{group.name}</strong>
                      </td>
                      <td>
                        {group.description || <span className="text-muted">No description</span>}
                      </td>
                      <td className="text-center">
                        <Badge bg="info">
                          {Array.isArray(group.user_ids) ? group.user_ids.length : 0}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              Click to {group.is_active ? 'deactivate' : 'activate'}
                            </Tooltip>
                          }
                        >
                          <Badge
                            bg={group.is_active ? 'success' : 'secondary'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleToggleStatus(group)}
                          >
                            {group.is_active ? (
                              <>
                                <FaCheckCircle className="me-1" /> Active
                              </>
                            ) : (
                              <>
                                <FaTimesCircle className="me-1" /> Inactive
                              </>
                            )}
                          </Badge>
                        </OverlayTrigger>
                      </td>
                      <td className="text-center text-muted small">
                        {formatDate(group.createddate)}
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Edit Group</Tooltip>}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditGroup(group)}
                            >
                              <FaEdit />
                            </Button>
                          </OverlayTrigger>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Delete Group</Tooltip>}
                          >
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteClick(group)}
                            >
                              <FaTrash />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <UserGroupForm
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleSaveGroup}
        editingGroup={editingGroup}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the group <strong>{groupToDelete?.name}</strong>?</p>
          <p className="text-muted">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            <FaTrash className="me-2" />
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserGroupManager;
